import { DependencyList } from 'react';
import { useEffect } from 'react';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';

/**
 * An async-friendly version of `useEffect`.
 *
 * The effect function will be called with an `isAlive` function as its first
 * argument. This function can be used to check if the component is still mounted
 * and therefore if its dependencies are safe to use.
 *
 * Reading your dependencies within the callback is generally safe. Writing to them
 * and / or calling them however is not. As a general rule, every time you try to
 * modify / call a dependency after having awaited on an async function, you should
 * use the `isAlive` function argument to determine if it's safe to do so.
 *
 * Sample usage:
 * const cb = useAsyncEffect(async (isAlive) => {
 *   if (isAlive())
 *     setState(someState);
 *
 *   const x = await doSomethingAsync(arg);
 *
 *   if (isAlive())
 *     setState(x);
 * }, [setState]);
 */
export function useAsyncEffect(
  effect: (isAlive: IsAliveFn) => Promise<void>,
  deps: DependencyList,
): void {
  const isAlive = useAlive();

  return useEffect(() => {
    // Call our effect and provide a function to it as its first argument
    // that it can use to check if the component is still mounted, and therefore
    // if its dependencies are safe to use.
    effect(isAlive).catch((err) => {
      // Log this error.
      uiAppLogger.error(err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, isAlive]);
}
